import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as featureFlagsSelectors from 'utils/featureFlags/selectors';
import { getProfile } from 'data/profile/selectors';
import { getAuthSession } from 'utils/auth/utils';
import { getActiveSubscription } from 'data/subscriptions/subscriptionsSelectors';
import { getFinancialInstitutionsPendo } from 'data/accounts/selectors';
import { Config } from 'utils/webappConfig';
import { getCurrentDataset } from 'data/datasets/selectors';
import { getLogger } from 'utils/logger';

const log = getLogger('utils/pendo.js');

function updatePendo({
  profile,
  authSession,
  subscription,
  addFiDefaultProvider,
  financialInstitutions,
  currentDataset,
}) {
  if (window.pendo && authSession && profile && subscription) {
    // Call this whenever information about your visitors becomes available
    // Please use Strings, Numbers, or Bools for value types.
    const metaData = {
      visitor: {
        id: authSession.qcsId, // Required if user is logged in
        // email: profile.username, // Recommended if using Pendo Feedback, or NPS Email
        // full_name: `${profile.firstName} ${profile.lastName}`, // Recommended if using Pendo Feedback
        // role:         // Optional

        // You can add any additional visitor level key-values here,
        // as long as it's not one of the above reserved names.
        app_version: String(process.env.APP_VERSION),
        default_aggregator: addFiDefaultProvider,
        financial_institutions: financialInstitutions?.join(', '),
        financial_institutions_list: financialInstitutions,
        ...(currentDataset && { current_dataset_created: currentDataset.createdAt }),
      },

      account: {
        // id: subscription.productLineName, // Highly recommended
        // name: subscription.productLineName, // Optional
        // is_paying: subscription.active, // Recommended if using Pendo Feedback
        // monthly_value:// Recommended if using Pendo Feedback
        // planLevel: subscription.tierName, // Optional
        // planPrice:    // Optional
        // creationDate: subscription.effectiveAt, // Optional

        // You can add any additional account level key-values here,
        // as long as it's not one of the above reserved names.
        // expireOn: subscription.expireOn,
      },
    };
    log.info('\npendo meta data:\n', metaData)
    window.pendo.initialize(metaData);
  }
}

const Pendo = () => {
  const featureFlagsError = useSelector(featureFlagsSelectors.getError);
  const featureFlagsLoadPending = useSelector(featureFlagsSelectors.getLoadPending);
  const pendoEnabled = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'pendo'));
  const addFiDefaultProvider = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'addFiDefaultProvider'));
  const profile = useSelector(getProfile);
  const authSession = useSelector(getAuthSession);
  const subscription = useSelector(getActiveSubscription);
  const financialInstitutions = useSelector(getFinancialInstitutionsPendo);
  const currentDataset = useSelector(getCurrentDataset);

  useEffect(() => {
    if (!featureFlagsLoadPending || featureFlagsError) {
      if (!window.pendo && pendoEnabled && Config.common.pendo_api_key) {
        /* eslint-disable */
        (function(apiKey){
          (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=[];
            v=['initialize','identify','updateOptions','pageLoad'];for(w=0,x=v.length;w<x;++w)(function(m){
              o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
            y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
            z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
        })(Config.common.pendo_api_key);
        /* eslint-enable */

        updatePendo({ profile, authSession, subscription });
      }
    }
  }, [featureFlagsLoadPending, featureFlagsError, pendoEnabled, profile, authSession, subscription]);

  useEffect(() => updatePendo({
    profile,
    authSession,
    subscription,
    addFiDefaultProvider,
    financialInstitutions,
    currentDataset,
  })
  , [
    profile,
    authSession,
    subscription,
    addFiDefaultProvider,
    financialInstitutions,
    currentDataset,
  ]);

  return null;
};

export default Pendo;


